import React, { useState, useEffect, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DispositionWorkflowBuilder, StepConfig } from './DispositionWorkflowBuilder';
import WorkflowStepsPreview from './WorkflowStepsPreview';
import { WorkflowSchedule } from './WorkflowSchedule';
import { SiteFormValues } from '../siteSchema';
import {
  ProjectFullDto,
  DispositionWorkflowConfig,
  Step,
  WorkflowConfig,
  WeeklySchedule,
  DaySchedule,
  TimeRange,
} from '../siteApi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { Form } from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EnumAlertStatus } from '@/lib/api/types.gen';

interface DispositionWorkflowTabProps {
  form: UseFormReturn<SiteFormValues>;
  siteData?: ProjectFullDto;
  onSubmit: (data: SiteFormValues) => void;
}

export function DispositionWorkflowTab({
  form,
  siteData,
  onSubmit,
}: DispositionWorkflowTabProps): JSX.Element {
  // State for managing workflows
  const [workflowsByStatus, setWorkflowsByStatus] = useState<Record<string, WorkflowConfig[]>>({});
  const [selectedStatus, setSelectedStatus] = useState<EnumAlertStatus>(EnumAlertStatus.NoThreat);
  const [selectedWorkflowIndex, setSelectedWorkflowIndex] = useState<number>(0);
  const [workflowName, setWorkflowName] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<{
    status: EnumAlertStatus;
    index: number;
  } | null>(null);

  // Debug form errors
  useEffect(() => {
    if (form?.formState.errors && Object.keys(form.formState.errors).length > 0) {
      console.log('Form errors in DispositionWorkflowTab:', form.formState.errors);
    }
  }, [form?.formState.errors]);

  // Update form values when workflows change
  const updateFormValues = useCallback(
    (workflows: Record<string, WorkflowConfig[]>) => {
      if (!form) return;

      // Only update if there are workflows
      if (Object.keys(workflows).length > 0) {
        // Create a deep copy to avoid reference issues
        const formattedWorkflows: Record<string, WorkflowConfig[]> = {};

        // Format each status and its workflows
        Object.entries(workflows).forEach(([status, statusWorkflows]) => {
          // Only include statuses that have workflows
          if (statusWorkflows.length > 0) {
            formattedWorkflows[status] = statusWorkflows.map((workflow) => ({
              name: workflow.name,
              steps: workflow.steps.map((step, index) => ({
                stepNumber: index,
                hasNote: step.hasNote,
                hasCheckbox: step.hasCheckbox,
                title: step.title,
                instructions: step.instructions || '',
              })),
              isDefault: workflow.isDefault,
              schedule: workflow.schedule,
            }));
          }
        });

        // Update the form values
        form.setValue(
          'dispositionWorkflowConfig',
          formattedWorkflows as SiteFormValues['dispositionWorkflowConfig'],
          { shouldDirty: true }
        );
      } else {
        // Clear the form values if no workflows
        form.setValue(
          'dispositionWorkflowConfig', {},
          { shouldDirty: true }
        );
      }
    },
    [form]
  );

  // Initialize from form values or siteData
  useEffect(() => {
    // Function to initialize workflows from config
    const initializeFromConfig = (config: DispositionWorkflowConfig) => {
      if (!config) return;

      // Set the workflows by status
      setWorkflowsByStatus(config);

      // Set selected status to the first available one if any exist
      const statuses = Object.keys(config);
      if (statuses.length > 0) {
        const firstStatus = statuses[0] as EnumAlertStatus;
        setSelectedStatus(firstStatus);

        // Set the first workflow as selected if available
        if (config[firstStatus]?.length > 0) {
          setSelectedWorkflowIndex(0);
          setWorkflowName(config[firstStatus][0].name);
        }
      }
    };

    if (form) {
      const workflowConfig = form.getValues().dispositionWorkflowConfig;
      if (workflowConfig) {
        initializeFromConfig(workflowConfig as unknown as DispositionWorkflowConfig);
      } else {
        // Initialize with empty workflows
        setWorkflowsByStatus({});
      }
    } else if (siteData?.dispositionWorkflowConfig) {
      initializeFromConfig(siteData.dispositionWorkflowConfig);
    } else {
      // Initialize with empty workflows
      setWorkflowsByStatus({});
    }
  }, [form, siteData]);

  // Get available statuses (those without workflows)
  const getAvailableStatuses = useCallback(() => {
    const usedStatuses = Object.keys(workflowsByStatus);
    return Object.values(EnumAlertStatus).filter((status) => !usedStatuses.includes(status));
  }, [workflowsByStatus]);

  // Handle status change
  const handleStatusChange = (status: EnumAlertStatus) => {
    setSelectedStatus(status);

    // Reset workflow index if changing status
    if (workflowsByStatus[status]?.length > 0) {
      setSelectedWorkflowIndex(0);
      setWorkflowName(workflowsByStatus[status][0].name);
    } else {
      setSelectedWorkflowIndex(-1);
      setWorkflowName('');
    }
  };

  // Add a new workflow for a status
  const addWorkflow = (status: EnumAlertStatus) => {
    // Create a default workflow name
    const existingCount = workflowsByStatus[status]?.length || 0;
    const newWorkflowName = `Workflow ${existingCount + 1}`;
    const isFirstWorkflow = existingCount === 0;

    // Create a new workflow for this status
    const newWorkflow: WorkflowConfig = {
      name: newWorkflowName,
      steps: [],
      isDefault: isFirstWorkflow, // Set as default if it's the first workflow for this status
    };

    // Add the workflow to the status
    setWorkflowsByStatus((prev) => {
      const updatedWorkflows = { ...prev };
      if (!updatedWorkflows[status]) {
        updatedWorkflows[status] = [];
      }
      updatedWorkflows[status] = [...updatedWorkflows[status], newWorkflow];

      // Update form values with the new state
      updateFormValues(updatedWorkflows);

      return updatedWorkflows;
    });

    // Set the workflow name and index after state update
    setWorkflowName(newWorkflowName);

    // Use the current length to determine the new index
    const newIndex = existingCount;
    setSelectedWorkflowIndex(newIndex);
  };

  // Remove a workflow
  const confirmDeleteWorkflow = (status: EnumAlertStatus, index: number) => {
    setWorkflowToDelete({ status, index });
    setDeleteDialogOpen(true);
  };

  const deleteWorkflow = () => {
    if (!workflowToDelete) return;

    const { status, index } = workflowToDelete;

    setWorkflowsByStatus((prev) => {
      const updatedWorkflows = { ...prev };

      // Remove the workflow at the specified index
      if (updatedWorkflows[status] && updatedWorkflows[status].length > index) {
        updatedWorkflows[status] = [
          ...updatedWorkflows[status].slice(0, index),
          ...updatedWorkflows[status].slice(index + 1),
        ];

        // If no workflows left for this status, remove the status
        if (updatedWorkflows[status].length === 0) {
          delete updatedWorkflows[status];
        }
      }

      updateFormValues(updatedWorkflows);
      return updatedWorkflows;
    });

    // Reset selected workflow index if needed
    if (selectedStatus === status && selectedWorkflowIndex >= index) {
      const newWorkflows = workflowsByStatus[status];
      if (newWorkflows && newWorkflows.length > 0) {
        // If there are still workflows, select the previous one or the first one
        const newIndex = Math.max(0, selectedWorkflowIndex - 1);
        setSelectedWorkflowIndex(newIndex);
        setWorkflowName(newWorkflows[newIndex]?.name || '');
      } else {
        // If no workflows left, reset
        setSelectedWorkflowIndex(-1);
        setWorkflowName('');
      }
    }

    // Close the dialog
    setDeleteDialogOpen(false);
    setWorkflowToDelete(null);
  };

  // Handle workflow name change
  const handleWorkflowNameChange = (name: string) => {
    setWorkflowName(name);

    // Update the workflow name in the state
    setWorkflowsByStatus((prev) => {
      const updatedWorkflows = { ...prev };
      if (
        updatedWorkflows[selectedStatus] &&
        updatedWorkflows[selectedStatus][selectedWorkflowIndex]
      ) {
        updatedWorkflows[selectedStatus][selectedWorkflowIndex].name = name;
      }
      updateFormValues(updatedWorkflows);
      return updatedWorkflows;
    });
  };

  // Handle steps change for a specific workflow
  const handleStepsChange = (steps: StepConfig[]) => {
    // Map StepConfig to Step
    const newSteps: Step[] = steps.map((step, index) => ({
      stepNumber: index + 1,
      hasNote: step.hasNote,
      hasCheckbox: step.hasCheckbox,
      title: step.title,
      instructions: step.instructions,
    }));

    // Update the current workflow's steps
    setWorkflowsByStatus((prev) => {
      const updatedWorkflows = { ...prev };
      if (
        updatedWorkflows[selectedStatus] &&
        updatedWorkflows[selectedStatus][selectedWorkflowIndex]
      ) {
        updatedWorkflows[selectedStatus][selectedWorkflowIndex].steps = newSteps;
      }
      updateFormValues(updatedWorkflows);
      return updatedWorkflows;
    });
  };

  // Handle schedule change
  const handleScheduleChange = (workflowIndex: number, schedule: WeeklySchedule) => {
    setWorkflowsByStatus((prev) => {
      const updatedWorkflows = { ...prev };
      if (updatedWorkflows[selectedStatus] && updatedWorkflows[selectedStatus][workflowIndex]) {
        updatedWorkflows[selectedStatus][workflowIndex].schedule = schedule;
      }
      updateFormValues(updatedWorkflows);
      return updatedWorkflows;
    });
  };

  // Handle changing the default workflow for a status
  const handleDefaultWorkflowChange = (workflowIndex: number) => {
    setWorkflowsByStatus((prev) => {
      const updatedWorkflows = { ...prev };

      // Only proceed if the status and workflow exist
      if (!updatedWorkflows[selectedStatus]) return prev;

      // Update all workflows for this status to set isDefault to false
      updatedWorkflows[selectedStatus] = updatedWorkflows[selectedStatus].map(
        (workflow, index) => ({
          ...workflow,
          isDefault: index === workflowIndex, // Set isDefault to true only for the selected workflow
        })
      );

      // Update form values with the new state
      updateFormValues(updatedWorkflows);

      return updatedWorkflows;
    });
  };

  // Convert the current workflow's steps to StepConfig[] format for the builder
  const getCurrentWorkflowSteps = (): StepConfig[] => {
    if (
      selectedWorkflowIndex >= 0 &&
      workflowsByStatus[selectedStatus]?.[selectedWorkflowIndex]?.steps
    ) {
      return workflowsByStatus[selectedStatus][selectedWorkflowIndex].steps.map((step, index) => ({
        key: `step-${selectedStatus}-${selectedWorkflowIndex}-${index}`,
        title: step.title,
        instructions: step.instructions || '',
        hasNote: step.hasNote,
        hasCheckbox: step.hasCheckbox,
        stepNumber: step.stepNumber,
      }));
    }
    return [];
  };

  console.log(form.getValues());
  return (
    <section
      id="disposition-workflow"
      className="mt-6 scroll-mt-[8rem] rounded-xl border border-gray-800 bg-[#111]"
    >
      {form && (
        <Form {...form}>
          <form id="site-form" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-6 p-6">
              {/* Status selection with visual indicators */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-400">ALERT STATUS</h3>

                  {getAvailableStatuses()
                    .filter((status) => [
                      EnumAlertStatus.New,
                      EnumAlertStatus.Intruder,
                      EnumAlertStatus.FalsePositive,
                      EnumAlertStatus.NoThreat,
                      EnumAlertStatus.Other,
                      EnumAlertStatus.NeedsService,
                      EnumAlertStatus.SafetyEvent,
                    ].includes(status))
                    .includes(selectedStatus) && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => addWorkflow(selectedStatus)}
                      className="border-gray-700 bg-[#222] text-white hover:bg-[#333]"
                    >
                      <Plus className="mr-1 h-4 w-4" /> Create Workflow
                    </Button>
                  )}
                </div>

                <Select
                  value={selectedStatus}
                  onValueChange={(value) => handleStatusChange(value as EnumAlertStatus)}
                >
                  <SelectTrigger className="w-full border-gray-700 bg-[#222] text-white">
                    <SelectValue placeholder="Select alert status" />
                  </SelectTrigger>
                  <SelectContent className="border-gray-700 bg-[#222] text-white">
                    {/* Section 1: Statuses with configured workflows */}
                    {Object.keys(workflowsByStatus).length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-medium text-gray-400">
                          Configured Workflows
                        </div>
                        {Object.keys(workflowsByStatus).map((status) => (
                          <SelectItem key={status} value={status}>
                            <div className="flex items-center">
                              <div
                                className={`mr-2 h-2 w-2 rounded-full ${
                                  status === EnumAlertStatus.Intruder
                                    ? 'bg-red-600'
                                    : status === EnumAlertStatus.SuspiciousPerson
                                      ? 'bg-yellow-300/20'
                                      : 'bg-gray-300/20'
                                }`}
                              />
                              {status.replace(/([A-Z])/g, ' $1').trim()}
                              <span className="ml-2 text-xs text-gray-500">
                                ({workflowsByStatus[status].length} workflow
                                {workflowsByStatus[status].length !== 1 ? 's' : ''})
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}

                    {/* Divider between sections */}
                    {Object.keys(workflowsByStatus).length > 0 &&
                      getAvailableStatuses().length > 0 && (
                        <div className="my-1 border-t border-gray-700" />
                      )}

                    {/* Section 2: Available statuses without workflows */}
                    {getAvailableStatuses().length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-medium text-gray-400">
                          Available Statuses
                        </div>
                        {getAvailableStatuses()
                          .filter((status) => [
                            EnumAlertStatus.New,
                            EnumAlertStatus.Intruder,
                            EnumAlertStatus.FalsePositive,
                            EnumAlertStatus.NoThreat,
                            EnumAlertStatus.Other,
                            EnumAlertStatus.NeedsService,
                            EnumAlertStatus.SafetyEvent,
                          ].includes(status))
                          .map((status) => (
                            <SelectItem key={status} value={status}>
                              <div className="flex items-center">
                                <div
                                  className={`mr-2 h-2 w-2 rounded-full ${
                                    status === EnumAlertStatus.Intruder
                                      ? 'bg-red-600'
                                      : status === EnumAlertStatus.SuspiciousPerson
                                        ? 'bg-yellow-300/20'
                                        : 'bg-gray-300/20'
                                  }`}
                                />
                                {status.replace(/([A-Z])/g, ' $1').trim()}
                              </div>
                            </SelectItem>
                          ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Combined workflow management and steps section */}
              {workflowsByStatus[selectedStatus] ? (
                <>
                  <div className="space-y-6 rounded-md border border-gray-800 bg-[#161616] p-4">
                    {/* Workflow selection and management */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium uppercase text-gray-400">
                          {selectedStatus.replace(/([A-Z])/g, ' $1').trim()} Workflow
                          {workflowsByStatus[selectedStatus].length > 1 &&
                            ` (${selectedWorkflowIndex + 1}/${workflowsByStatus[selectedStatus].length})`}
                        </h3>

                        <div className="flex items-center gap-2">
                          {workflowsByStatus[selectedStatus].length > 1 && (
                            <Select
                              value={selectedWorkflowIndex.toString()}
                              onValueChange={(value) => {
                                const index = parseInt(value);
                                setSelectedWorkflowIndex(index);
                                setWorkflowName(workflowsByStatus[selectedStatus][index].name);
                              }}
                            >
                              <SelectTrigger className="w-[180px] border-gray-700 bg-[#222] text-white">
                                <SelectValue placeholder="Select workflow" />
                              </SelectTrigger>
                              <SelectContent className="border-gray-700 bg-[#222] text-white">
                                {workflowsByStatus[selectedStatus].map((workflow, index) => (
                                  <SelectItem key={index} value={index.toString()}>
                                    {workflow.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => addWorkflow(selectedStatus)}
                            className="h-9 border-gray-700 bg-[#222] text-white hover:bg-[#333]"
                          >
                            <Plus className="mr-1 h-4 w-4" /> Add
                          </Button>
                        </div>
                      </div>

                      {workflowsByStatus[selectedStatus].length > 0 &&
                        selectedWorkflowIndex >= 0 && (
                          <div className="flex items-center gap-4">
                            <div className="flex-1">
                              <label className="mb-2 block text-sm font-medium text-gray-300">
                                Workflow Name
                              </label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={workflowName}
                                  onChange={(e) => handleWorkflowNameChange(e.target.value)}
                                  className="flex-1 rounded-md border border-gray-700 bg-[#222] px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                  placeholder="Enter workflow name"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() =>
                                    confirmDeleteWorkflow(selectedStatus, selectedWorkflowIndex)
                                  }
                                  className="h-9 border-gray-700 bg-[#222] text-red-400 hover:bg-[#333] hover:text-red-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Steps section with preview */}
                    {workflowsByStatus[selectedStatus].length > 0 && selectedWorkflowIndex >= 0 ? (
                      <div className="flex gap-6">
                        <div className="flex-1 space-y-4">
                          <DispositionWorkflowBuilder
                            steps={getCurrentWorkflowSteps()}
                            status={selectedStatus}
                            onChange={handleStepsChange}
                          />
                        </div>
                        <div className="w-1/3">
                          <WorkflowStepsPreview
                            steps={getCurrentWorkflowSteps()}
                            workflowName={workflowName}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 text-center text-gray-400">
                        No workflows available for{' '}
                        {selectedStatus.replace(/([A-Z])/g, ' $1').trim()}. Add one to get started.
                      </div>
                    )}
                  </div>

                  {selectedStatus &&
                    workflowsByStatus[selectedStatus]?.length > 0 &&
                    selectedWorkflowIndex >= 0 && (
                      <div className="mt-6 rounded-md border border-gray-800 bg-[#161616] p-4">
                        <WorkflowSchedule
                          workflows={workflowsByStatus[selectedStatus] || []}
                          selectedWorkflowIndex={selectedWorkflowIndex}
                          onScheduleChange={handleScheduleChange}
                          onDefaultWorkflowChange={handleDefaultWorkflowChange}
                        />
                      </div>
                    )}
                </>
              ) : (
                <div className="rounded-md border border-gray-800 bg-[#161616] p-6 text-center text-gray-400">
                  {getAvailableStatuses().includes(selectedStatus) ? (
                    <>
                      Click the Create Workflow button above to add a workflow for{' '}
                      {selectedStatus.replace(/([A-Z])/g, ' $1').trim()}
                    </>
                  ) : (
                    <>No alert status selected. Please select an alert status above.</>
                  )}
                </div>
              )}
            </div>
          </form>
        </Form>
      )}
      {!form && (
        <div className="p-6 text-center text-gray-400">
          Form not available. Unable to edit disposition workflows.
        </div>
      )}

      {/* Confirmation dialog for deleting workflows */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="border-gray-800 bg-[#111] text-white">
          <DialogHeader>
            <DialogTitle>Delete Workflow</DialogTitle>
            <DialogDescription className="text-gray-400">
              Are you sure you want to delete this workflow? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-gray-700 bg-[#222] text-white hover:bg-[#333]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={deleteWorkflow}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default DispositionWorkflowTab;
